import { AssetType, ConversionType, ExternalDestinationSchema, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
import { conversionIconProvider, type IconAsset } from "#lib/iconProvider.js";
import { formatPhoneNumber, normalizePhoneNumber } from "#lib/phonenumber.js";
import { fromJson } from "#lib/protobuf-compat.js";
import { insecureProtocol, mailtoProtocol, secureProtocol, smsProtocol, telProtocol } from "#lib/url.js";

const bodyDelimiter = ";?&body=";

export function decodePhoneNumberDestination(destination: string): { phoneNumber: string; message: string } {
	if (!(destination?.startsWith(telProtocol) || destination?.startsWith(smsProtocol))) {
		return { phoneNumber: "", message: "" };
	}

	const currentDelimiter = bodyDelimiter;
	const legacyDelimiter = "?&body=";
	const isSMSLink = destination.startsWith(smsProtocol);
	const destinationWithoutProtocol = destination?.trim().slice(4);

	if (isSMSLink && (destinationWithoutProtocol.includes(legacyDelimiter) || destinationWithoutProtocol.includes(currentDelimiter))) {
		// The legacy delimiter is a substring of the current (correct)
		// delimiter, so we want to check for the current one first.
		const hasCurrentDelimiter = destinationWithoutProtocol.includes(currentDelimiter);
		const [phoneNumber, message] = destinationWithoutProtocol.split(hasCurrentDelimiter ? currentDelimiter : legacyDelimiter);
		return { phoneNumber: decodeURIComponent(phoneNumber), message: decodeURIComponent(message) };
	}

	return { phoneNumber: decodeURIComponent(destinationWithoutProtocol.split("?")[0]), message: "" };
}

export function decodeEmailDestination(destination: string): { emailAddress: string; subject: string; body: string; cc: string; bcc: string } {
	if (!destination.startsWith(mailtoProtocol)) {
		return { emailAddress: "", subject: "", body: "", cc: "", bcc: "" };
	}
	const cleanedEmailString = destination.trim().slice(7); //remove mailto:
	const emailParts = cleanedEmailString.split("?");
	const emailAddress = emailParts?.[0];
	const subjectParts = emailParts?.[1]?.split("&");
	let subject = "";
	let body = "";
	let cc = "";
	let bcc = "";
	subjectParts?.forEach(part => {
		if (part.startsWith("subject=")) {
			subject = part.slice(8);
		} else if (part.startsWith("body=")) {
			body = part.slice(5);
		} else if (part.startsWith("cc=")) {
			cc = part.slice(3);
		} else if (part.startsWith("bcc=")) {
			bcc = part.slice(4);
		}
	});

	return { emailAddress, subject: decodeURIComponent(subject), body: decodeURIComponent(body), cc: decodeURIComponent(cc), bcc: decodeURIComponent(bcc) };
}

export function encodeSMSDestination(phoneNumber: string, message: string): string {
	const cleanedPhoneNumber = removeFormattingFromNumber(phoneNumber);
	const encodedMessage = encodeURIComponentOutsideOfDataProperties(message);
	return `${smsProtocol}${cleanedPhoneNumber}${message.length ? `${bodyDelimiter}${encodedMessage}` : ""}`;
}

function formatPhoneNumberWithDashes(value: string) {
	if (stringContainsDataProperties(value)) return value;
	return formatPhoneNumber(value);
}

function stringContainsDataProperties(value: string): boolean {
	return /\{\{[^{}]*\}\}/.test(value);
}

function removeFormattingFromNumber(value: string) {
	if (stringContainsDataProperties(value)) return value;
	return normalizePhoneNumber(value).replace(/[^0-9+]/g, "");
}

export function translateAdvancedDestinationToString(destination: string): string {
	if (destination.startsWith(telProtocol) || destination.startsWith(smsProtocol)) {
		const { phoneNumber } = decodePhoneNumberDestination(destination);
		const phoneNumberString = formatPhoneNumberWithDashes(phoneNumber);
		if (destination.startsWith(telProtocol)) return `Call ${phoneNumberString}`;
		return phoneNumberString.length ? `Send ${phoneNumberString} a text` : "Send a text";
	} else if (destination.startsWith(mailtoProtocol)) {
		const { emailAddress } = decodeEmailDestination(destination);
		return emailAddress.length ? `Send ${emailAddress} an email` : "Send an email";
	}
	return destination;
}

function getIconFromUrl(url: string, defaultType?: string): IconAsset {
	const destinationType = getDestinationTypeFromString(url, defaultType);
	switch (destinationType) {
		case "call":
			return { icon: "phone", iconSet: "heroicons" };
		case "sms":
			return { icon: "chat-bubble-left-ellipsis", iconSet: "heroicons" };
		case "email":
			return { icon: "envelope", iconSet: "heroicons" };
		case "file":
			return { icon: "document", iconSet: "heroicons" };
		case "app_deep_link":
			return { icon: "squares-2x2", iconSet: "heroicons" };
		default:
			return { icon: "link", iconSet: "heroicons" };
	}
}

/**
 * Use the destination's URL to determine its type.
 *
 * We can use the presence of protocols to determine the type but need to
 * default to app_deep_link because it has the least identifiable pieces.
 */
function getDestinationTypeFromString(destination: string | undefined, defaultType = "app_deep_link"): string {
	if (destination) {
		if (destination.startsWith(telProtocol)) return "call";
		if (destination.startsWith(smsProtocol)) return "sms";
		if (destination.startsWith(mailtoProtocol)) return "email";
		if (destination.startsWith(secureProtocol) || destination.startsWith(insecureProtocol) || (destination.includes("{{") && destination.includes("}}"))) return "website";
	}
	return defaultType;
}

function encodeURIComponentOutsideOfDataProperties(message: string): string {
	const encodedMessage = message.replace(/{{(.*?)}}|([^{{}}]+)/g, (match, bracketed, nonBracketed) => {
		if (bracketed) {
			// If inside {{ }}, return it as is
			return `{{${bracketed}}}`;
		} else if (nonBracketed) {
			// Encode the non-bracketed part
			return encodeURIComponent(nonBracketed);
		}
		return match; // Fallback
	});
	return encodedMessage;
}

export function getAttemptedIconMatch(asset: AssetRef | undefined, defaultType?: string): IconAsset | undefined {
	if (asset?.type === AssetType.EXTERNAL_DESTINATION) {
		const external = fromJson(ExternalDestinationSchema, asset.config ?? {}, { ignoreUnknownFields: true });
		return getIconFromUrl(external.url, defaultType);
	}
	return conversionIconProvider.get(asset?.conversionType ?? ConversionType.UNSPECIFIED);
}
